import { tagged } from "@mirage/service-logging";
import { of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { createConversationResponseObservable } from "./context_observables";

import type {
  AgentId,
  ConversationClientContext,
  DocumentInfo,
} from "@mirage/service-conversation/types";
import type { Observable } from "rxjs";
import type { ConversationsAssistantResponseWithJsonMessages } from "./context_observables";
import type { ConversationsChatMessage } from "./gen/conversations_pb";
import type { ConversationsResult, ConversationsResultError } from "./types";

const logger = tagged("conversations_grpc");

export function getConversationResponse(
  userMessage: string,
  serverConversationId: string | undefined,
  history?: ConversationsChatMessage[],
  experimentSetting?: string,
  source?: string,
  modelChoice?: string,
  agentPayload?: string,
  attachments?: DocumentInfo[],
  agentIds?: AgentId[],
  rephrased?: string,
  clientContext?: ConversationClientContext,
  skipSearch?: boolean,
): Observable<ConversationsResult | ConversationsResultError> {
  const conversation$ = createConversationResponseObservable(
    userMessage,
    serverConversationId,
    history,
    experimentSetting,
    source,
    modelChoice,
    agentPayload,
    attachments,
    agentIds,
    rephrased,
    clientContext,
    skipSearch,
  );

  return conversation$.pipe(
    map(
      (
        response: ConversationsAssistantResponseWithJsonMessages,
      ): ConversationsResult => {
        return {
          serverConversationId: response.conversationId,
          messages: response.messages ?? [],
          timestamp: new Date().toISOString(),
          suggestedQueries: response.suggestedQueries ?? [],
          progressUpdates:
            response.progressUpdates?.map((update) => update.step) ?? [],
          unprocessedResponse: response.unprocessedResponse,
          assistRequestId: response.requestId,
        };
      },
    ),
    catchError((error) => {
      logger.error("getConversationResponse error", error);
      const errorSummary: ConversationsResultError = {
        error: "Failed to fetch conversation response. " + error,
        timestamp: new Date().toISOString(),
      };
      return of(errorSummary);
    }),
  );
}
